import React from 'react'
import {
  Alert,
  Button,
  Stack,
  Typography,
  Link,
  TextField,
  CircularProgress,
} from '@mui/material'
import * as Yup from 'yup'
import { useState, useEffect, useMemo } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { blue } from '@mui/material/colors'
import { TitledPaper, TypedFormik } from '@dai/components'
import { ActionCodeInfo } from 'firebase/auth'

interface HandleResetPasswordProps {
  oobCode: string
  signInUrl: string
  passwordSchema: Yup.StringSchema
  checkActionCode: (code: string) => Promise<ActionCodeInfo>
  confirmPasswordReset: (code: string, password: string) => Promise<void>
}

const HandleResetPassword: React.FC<HandleResetPasswordProps> = ({
  oobCode,
  signInUrl,
  passwordSchema,
  checkActionCode,
  confirmPasswordReset,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState<string | null>(null)
  const [validatingCode, setValidatingCode] = useState<boolean>(false)
  const [codeError, setCodeError] = useState<string | null>(null)

  const ERROR_MESSAGES: Record<string, string> = useMemo(
    () => ({
      'auth/expired-action-code':
        'Your request to reset your password has expired or the link has already been used.',
      'auth/invalid-action-code':
        'Your request to reset your password has expired or the link has already been used.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/user-not-found': 'User not found.',
    }),
    [],
  )

  useEffect(() => {
    const verifyCode = async (code: string) => {
      try {
        setValidatingCode(true)
        const actionCodeInfo = await checkActionCode(code)
        setEmail(actionCodeInfo.data.email || null)
        setCodeError(null)
      } catch (error: any) {
        const message =
          ERROR_MESSAGES[error.code] || error.message || 'Invalid reset link'
        setCodeError(message)
      } finally {
        setValidatingCode(false)
      }
    }
    verifyCode(oobCode)
  }, [oobCode, checkActionCode, ERROR_MESSAGES])

  if (validatingCode) {
    return (
      <TitledPaper
        title="Reset Password"
        subtitle="Verifying your reset link..."
      >
        <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
          <CircularProgress />
        </Stack>
      </TitledPaper>
    )
  }

  if (codeError) {
    return (
      <TitledPaper
        title="Reset Password"
        subtitle="Try resetting your password again"
      >
        <Stack spacing={4}>
          <Alert severity="error">{codeError}</Alert>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={() =>
              navigate(location.pathname + '?mode=resetPassword', {
                state: { from: location.pathname + location.search },
                replace: true,
              })
            }
          >
            Request a new link
          </Button>
          <Typography variant="body2" color="text.secondary" align="center">
            Remember your password?{' '}
            <Link component={RouterLink} to={signInUrl}>
              Sign in
            </Link>
          </Typography>
        </Stack>
      </TitledPaper>
    )
  }

  return (
    <TitledPaper
      title="Reset Password"
      subtitle={
        <>
          Please enter your new password for{' '}
          <span style={{ color: blue[500] }}>{email}</span>
        </>
      }
    >
      <TypedFormik
        initialValues={{
          password: '',
          confirmPassword: '',
        }}
        validationSchema={Yup.object().shape({
          password: passwordSchema.required('Required'),
          confirmPassword: Yup.string().oneOf(
            [Yup.ref('password')],
            'Passwords must match',
          ),
        })}
        onSubmit={async (values, { setStatus }) => {
          try {
            await confirmPasswordReset(oobCode, values.password)
            setStatus({
              success: true,
              message: 'Your password has been reset successfully.',
            })
          } catch (error: any) {
            const message =
              ERROR_MESSAGES[error.code] ||
              error.message ||
              'Something went wrong'
            setStatus({ success: false, message })
          }
        }}
      >
        {({
          status,
          errors,
          handleSubmit,
          isSubmitting,
          handleBlur,
          handleChange,
          values,
          touched,
        }) => {
          return (
            <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Stack spacing={4}>
                {status?.message && (
                  <Alert severity={status.success ? 'success' : 'warning'}>
                    {status.message}
                  </Alert>
                )}
                {status?.success ? (
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    sx={{ mt: 2 }}
                    component={RouterLink}
                    to={signInUrl}
                  >
                    Sign in
                  </Button>
                ) : (
                  <>
                    <TextField
                      type="password"
                      name="password"
                      label="New password"
                      value={values.password}
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />

                    <TextField
                      type="password"
                      name="confirmPassword"
                      label="Confirm password"
                      value={values.confirmPassword}
                      error={Boolean(
                        touched.confirmPassword && errors.confirmPassword,
                      )}
                      fullWidth
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />

                    <Button
                      fullWidth
                      color="primary"
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      sx={{ mt: 2 }}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                    >
                      Reset Password
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      Remember your password?{' '}
                      <Link component={RouterLink} to={signInUrl}>
                        Sign in
                      </Link>
                    </Typography>
                  </>
                )}
              </Stack>
            </form>
          )
        }}
      </TypedFormik>
    </TitledPaper>
  )
}

export default HandleResetPassword
